import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["status"];
  static values = {
    checkUrl: { type: String, default: "" },
    redirectUrl: { type: String, default: "" },
    maxChecks: { type: Number, default: 90 },
    interval: { type: Number, default: 1000 },
    checkField: { type: String, default: "ai_response" },
  };

  connect() {
    this.checkCount = 0;
    this.currentStepIndex = 0;
    this.steps = this.getSteps();
    this.startStepProgression();
    this.startChecking();
  }

  disconnect() {
    this.stopChecking();
  }

  getSteps() {
    const stepsList = this.element.querySelector("ul");
    if (!stepsList) return [];

    return Array.from(stepsList.querySelectorAll("li")).map((li) => ({
      element: li,
      text: li.textContent.trim(),
      spinnerIcon: li.querySelector('[data-icon="spinner"]'),
      checkIcon: li.querySelector('[data-icon="check"]'),
    }));
  }

  startStepProgression() {
    if (this.steps.length <= 1) return;

    // Start with the first step active
    this.markStepActive(0);

    // Progress through steps with random delays
    this.scheduleNextStep(1);
  }

  scheduleNextStep(stepIndex) {
    if (stepIndex >= this.steps.length) return;

    const delay = this.getRandomDelay();
    setTimeout(() => {
      this.progressToStep(stepIndex);
      this.scheduleNextStep(stepIndex + 1);
    }, delay);
  }

  progressToStep(stepIndex) {
    if (stepIndex >= this.steps.length) return;

    // Mark previous step as completed
    if (stepIndex > 0) {
      this.markStepCompleted(stepIndex - 1);
    }

    // Mark current step as active (spinning)
    this.markStepActive(stepIndex);
  }

  markStepCompleted(stepIndex) {
    const step = this.steps[stepIndex];
    if (!step) return;

    // Show check icon and hide spinner
    if (step.spinnerIcon) {
      step.spinnerIcon.classList.add("hidden");
      step.spinnerIcon.classList.remove("block");
    }
    if (step.checkIcon) {
      step.checkIcon.classList.remove("hidden", "opacity-50");
      step.checkIcon.classList.add("block");
    }
  }

  markStepActive(stepIndex) {
    const step = this.steps[stepIndex];
    if (!step) return;

    // Show spinner and hide check icon
    if (step.spinnerIcon) {
      step.spinnerIcon.classList.remove("hidden");
      step.spinnerIcon.classList.add("block");
    }
    if (step.checkIcon) {
      step.checkIcon.classList.add("hidden");
      step.checkIcon.classList.remove("block");
    }
  }

  getRandomDelay() {
    // Random delay between 7-12 seconds for each step
    const minDelay = 7000;
    const maxDelay = 12000;
    return Math.random() * (maxDelay - minDelay) + minDelay;
  }

  startChecking() {
    this.timer = setTimeout(() => {
      this.checkProcessingStatus();
    }, this.intervalValue);
  }

  stopChecking() {
    this.checkCount = 0;
    if (this.timer) {
      clearTimeout(this.timer);
    }
  }

  async checkProcessingStatus() {
    if (this.checkCount >= this.maxChecksValue) {
      this.handleProcessingComplete();
      this.stopChecking();
      return;
    }

    try {
      const response = await fetch(this.checkUrlValue, {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (response.ok) {
        const data = await response.json();
        const checkValue = data[this.checkFieldValue];
        if (
          checkValue &&
          (typeof checkValue === "object"
            ? Object.keys(checkValue).length > 0
            : checkValue)
        ) {
          this.handleProcessingComplete();
          return;
        }
      }
    } catch (error) {
      this.stopChecking();
      return;
    }

    this.checkCount++;
    this.timer = setTimeout(() => {
      this.checkProcessingStatus();
    }, this.intervalValue);
  }

  handleProcessingComplete() {
    // Mark the last step as completed
    if (this.steps.length > 0) {
      this.markStepCompleted(this.steps.length - 1);
    }

    // Redirect after a short delay to show the completed state
    setTimeout(() => {
      if (this.redirectUrlValue) {
        window.location.href = this.redirectUrlValue;
      }
    }, 500);
  }
}
